import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { AppMode } from '../core/types.js';

interface Settings {
  readonly mode: AppMode;
  readonly piUrl: string;
  readonly statusEndpoint: string;
}

interface SettingsContextValue extends Settings {
  setMode(mode: AppMode): void;
  setPiUrl(url: string): void;
  setStatusEndpoint(url: string): void;
}

const SETTINGS_KEY_V1 = 'soluu-harness:settings:v1';
const SETTINGS_KEY = 'soluu-harness:settings:v2';

// SPEC §"Device + AP Mode UI" #1 — default Pi URL is http://synclet.local:3000.
// statusEndpoint default points at the Soluu production /status endpoint —
// the device pings this URL to confirm WAN reachability before reporting
// ON_INTERNET. Per-environment overrides go in Settings.
export const DEFAULT_STATUS_ENDPOINT =
  'https://jrjto9ji4f.execute-api.us-east-1.amazonaws.com/prod/status';

const DEFAULTS: Settings = {
  mode: 'device',
  piUrl: 'http://synclet.local:3000',
  statusEndpoint: DEFAULT_STATUS_ENDPOINT,
};

export function loadInitialSettings(storage: Storage | undefined): Settings {
  if (!storage) return DEFAULTS;
  try {
    const v2 = storage.getItem(SETTINGS_KEY);
    if (v2) {
      const parsed = JSON.parse(v2) as Partial<Settings>;
      return { ...DEFAULTS, ...parsed };
    }
    const v1 = storage.getItem(SETTINGS_KEY_V1);
    if (v1) {
      const parsed = JSON.parse(v1) as Partial<Settings>;
      // Carry forward mode + piUrl; statusEndpoint adopts the new default.
      return { ...DEFAULTS, ...parsed };
    }
  } catch {
    /* malformed payload — fall through to defaults */
  }
  return DEFAULTS;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(() =>
    loadInitialSettings(typeof window === 'undefined' ? undefined : window.localStorage),
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      /* quota / private mode — silently degrade */
    }
  }, [settings]);

  const setMode = useCallback(
    (mode: AppMode) => setSettings((s) => ({ ...s, mode })),
    [],
  );
  const setPiUrl = useCallback(
    (piUrl: string) => setSettings((s) => ({ ...s, piUrl })),
    [],
  );
  const setStatusEndpoint = useCallback(
    (statusEndpoint: string) => setSettings((s) => ({ ...s, statusEndpoint })),
    [],
  );

  const value = useMemo<SettingsContextValue>(
    () => ({ ...settings, setMode, setPiUrl, setStatusEndpoint }),
    [settings, setMode, setPiUrl, setStatusEndpoint],
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used inside <SettingsProvider>');
  return ctx;
}
